import { IBookInterval } from "@/domain/entities/lineInfo";
import { BookDateCode, BookIntervalStatus, BookTimeCode } from "@/types";
import dayjs from "dayjs";

function getBookDateDescription(
  bookDateCode = "",
  bookYear = 0,
  bookMonth = 0,
  bookDay = 0,
  bookIntervalStatus: BookIntervalStatus,
  bookWeek?: number,
  bookWeekdayName?: string
): string {
  switch (bookDateCode) {
    /** 고정일자 */
    case BookDateCode.FIXED: {
      return `${String(bookYear).substring(2)}/${bookMonth}/${bookDay}`;
    }

    /**
     * BDC-DAYAFT : 25일 오픈이면 26일 ~ 다음달 25일까지 예약 오픈되는걸 의미
     * BDC-DAY : 25일 오픈이면 다음달 working day 전체 오픈
     */
    case BookDateCode.DAYAFT:
    case BookDateCode.DAY: {
      // 현재시점에서 과거를 예약하는 경우는 없음
      if (bookMonth > 0) return "";

      if (bookIntervalStatus === BookIntervalStatus.END) {
        const monthStr = bookMonth === 0 ? "당월" : "전월";
        return `${monthStr} ${bookDay}일`;
      }

      const currentMonth = dayjs().format("M");
      return `${currentMonth}/${bookDay}`;
    }

    /** 월첫운행일  */
    case BookDateCode.FIRSTDAY: {
      if (!bookDay) return "";

      let bookDateDescription = `월 첫 운행일 ${Math.abs(bookDay)}일`;
      bookDateDescription += Math.sign(bookDay) > 0 ? " 후" : " 전";
      return bookDateDescription;
    }

    /** 탑승일기준 */
    case BookDateCode.BOARD: {
      if (!bookDay) return "";

      let bookDateDescription = `탑승 ${Math.abs(bookDay)}일`;
      bookDateDescription += Math.sign(bookDay) > 0 ? " 후" : " 전";
      return bookDateDescription;
    }

    /** 기점출발기준  */
    case BookDateCode.BEGIN: {
      if (!bookDay) return "";

      let bookDateDescription = `기점출발 ${Math.abs(bookDay)}일`;
      bookDateDescription += Math.sign(bookDay) > 0 ? " 후" : " 전";
      return bookDateDescription;
    }

    /** 종점도착기준 */
    case BookDateCode.NONE:
    case BookDateCode.END: {
      if (!bookDay) return "";

      let bookDateDescription = `종점도착 ${Math.abs(bookDay)}일`;
      bookDateDescription += Math.sign(bookDay) > 0 ? " 후" : " 전";
      return bookDateDescription;
    }

    /** 요일(오픈) */
    case BookDateCode.WEEK: {
      if (!bookWeek || !bookWeekdayName) return "";

      return bookWeek === -1
        ? `매주 ${bookWeekdayName}요일`
        : `${Math.abs(bookWeek)}주 전 ${bookWeekdayName}요일`;
    }

    /** 조건없음.종점도착일자(마감,취소) */
    case BookDateCode.WINDOW: {
      return "매일";
    }

    default:
      return "";
  }
}

function getTimeString(hour: number, minute: number): string {
  const hourString = hour ? `${Math.abs(hour)} 시간` : "";
  const minuteString = minute ? `${Math.abs(minute)}분` : "";
  const signString = (hour || minute) > 0 ? "후" : "전";
  return `${hourString}${minuteString} ${signString}`;
}

function getBookTimeDescription(
  bookTimeCode = "",
  bookHour = 0,
  bookMin = 0
): string {
  switch (bookTimeCode) {
    case BookTimeCode.FIXED: {
      const hour12 = bookHour > 12 ? bookHour - 12 : bookHour;
      const ampm = bookHour >= 12 ? "오후" : "오전";
      const hourString = `${ampm} ${hour12}시`;
      const minuteString = bookMin ? `${bookMin}분` : "";
      return `${hourString}${minuteString}`;
    }
    case BookTimeCode.BEGIN:
      return `기점출발 ${getTimeString(bookHour, bookMin)}`;
    case BookTimeCode.NONE:
    case BookTimeCode.END:
      return `종점도착 ${getTimeString(bookHour, bookMin)}`;
    case BookTimeCode.BOARD:
      return `승차장출발 ${getTimeString(bookHour, bookMin)}`;
    default:
      return "";
  }
}

function getBookOpenTargetStr(
  bookDateCode: BookDateCode,
  bookMonth: number,
  bookDay: number
): string {
  switch (bookDateCode) {
    case BookDateCode.DAYAFT:
    case BookDateCode.DAY: {
      if (!bookMonth) return "";
      const bookOpenMonth = dayjs()
        .add(Math.abs(bookMonth), "month")
        .format("M");
      return `${bookOpenMonth}월`;
    }
    case BookDateCode.WINDOW: {
      if (!bookDay) return "";
      return `${Math.abs(bookDay)}일 후까지`;
    }

    default:
      return "";
  }
}

function useGetBookBeginDateTimeStr(bookInterval?: IBookInterval): string {
  if (!bookInterval) return "";

  const {
    bookBeginDateCode,
    bookBeginYear,
    bookBeginMonth,
    bookBeginWeek,
    bookBeginWeekdayName,
    bookBeginDay,
    bookBeginTimeCode,
    bookBeginHour,
    bookBeginMin,
  } = bookInterval;

  const bookOpenTargetStr = getBookOpenTargetStr(
    bookBeginDateCode,
    bookBeginMonth,
    bookBeginDay
  );

  const bookBeginDateTimeStr = `${getBookDateDescription(
    bookBeginDateCode,
    bookBeginYear,
    bookBeginMonth,
    bookBeginDay,
    BookIntervalStatus.BEGIN,
    bookBeginWeek,
    bookBeginWeekdayName
  )} ${getBookTimeDescription(bookBeginTimeCode, bookBeginHour, bookBeginMin)}`;

  return `${bookBeginDateTimeStr}에 ${bookOpenTargetStr}`;
}

function useGetBookEndDateTimeStr(bookInterval?: IBookInterval): string {
  if (!bookInterval) return "";

  const {
    bookEndDateCode,
    bookEndYear,
    bookEndMonth,
    bookEndDay,
    bookEndTimeCode,
    bookEndHour,
    bookEndMin,
  } = bookInterval;

  let bookDateDescription = "";

  bookDateDescription = getBookDateDescription(
    bookEndDateCode,
    bookEndYear,
    bookEndMonth,
    bookEndDay,
    BookIntervalStatus.END
  );

  const bookEndDateTimeStr = `${bookDateDescription} ${getBookTimeDescription(
    bookEndTimeCode,
    bookEndHour,
    bookEndMin
  )}`;

  return bookEndDateTimeStr;
}

function useGetBookCancelDateTimeStr(bookInterval?: IBookInterval): string {
  if (!bookInterval) return "";

  const {
    bookCancelDateCode,
    bookCancelYear,
    bookCancelMonth,
    bookCancelDay,
    bookCancelTimeCode,
    bookCancelHour,
    bookCancelMin,
  } = bookInterval;

  const bookCancelDateTimeStr = `${getBookDateDescription(
    bookCancelDateCode,
    bookCancelYear,
    bookCancelMonth,
    bookCancelDay,
    BookIntervalStatus.CANCEL
  )} ${getBookTimeDescription(
    bookCancelTimeCode,
    bookCancelHour,
    bookCancelMin
  )}`;

  return bookCancelDateTimeStr;
}

export {
  useGetBookBeginDateTimeStr,
  useGetBookEndDateTimeStr,
  useGetBookCancelDateTimeStr,
};
